/**
 * A/B experiments, assigned once per browser and kept.
 *
 * One record in localStorage (`kindling.exp.v1`), an object keyed by
 * experiment, so a later experiment is one more key and not one more storage
 * slot. While an experiment runs, its arm is assigned at random the first time
 * it is asked about and never again: the arm is a fact about this browser, like
 * the first touch (lib/campaign.ts), and re-rolling it would move people between
 * arms in the middle of a funnel.
 *
 * `?fresh` does not reset it (it is not part of the profile or the campaign
 * record): a tester walks Day 1 again in the same arm, and switches arm on
 * purpose with the override.
 *
 * QA override: `?idea_step=off` / `?place_step=box` / `?landing=road` on any
 * url forces that arm and keeps it. They are read in main.tsx (initAnalytics)
 * before App.tsx takes the parameters off the url.
 *
 * Storage that refuses (private mode, blocked site data) still gets an arm
 * that holds for the life of the page, kept in memory here.
 */

const KEY = 'kindling.exp.v1';

export type Arm = 'on' | 'off';
/** The place step of the imagine starter: the Postcard Machine, or the box with sparks under it. */
export type PlaceArm = 'machine' | 'box';
/** What an ad visitor lands on: the first question (control), or the road itself. */
export type LandingArm = 'quiz' | 'road';

/**
 * `idea_step`: after the dream question, for the imagine starter, two quick
 * questions that get their imagination running — what kind of thing they want
 * to make (chips, no thinking), then what it is about (a box, real thinking) —
 * and the crew then draw them inside that idea's world instead of a dreamed-up
 * place. Owner, 2026-10-15. well/product/33 §8.
 *
 * DECIDED 2026-10-17: on for everyone. In the W2 ad round all five Day 1
 * finishers were in the on arm (5 of 30 on, 0 of 40 off). A browser the dice
 * put in `off` gets `on` from now on. `idea_step` still goes on every event
 * (analytics.ts) so the dashboards built on it keep reading. Its override is
 * kept under its own key, `idea_step_qa`, so a kept override is never mistaken
 * for an old dice roll.
 *
 * `place_step`: the starter's place step as a game (well/product/33 §9). Two
 * reels the machine spins, a place and where it is, and one in the middle
 * that never spins: the twist is the one word that is theirs to write. The
 * postcard above redraws on every landing, with them already standing in it.
 * Owner, 2026-10-16. The old box with its three sparks is the `box` arm.
 *
 * DECIDED 2026-10-29: everyone gets `machine`, which is the idea roll now
 * (#188). In W3 the old machine was the dead end (0 of 10 picked a place), and
 * the roll replaced it; the owner put every visitor on it for the next ad
 * round rather than split an already thin run. A browser the dice put in
 * `box` gets `machine` from now on. Like `idea_step`, the override is kept
 * under its own key, `place_step_qa`, so it is never mistaken for an old roll.
 *
 * `landing`: for an ad visitor on the imagine starter with no door
 * (`?door=plush&start=imagine`), the first screen. `quiz` is the W2 winner as
 * it stands (the question, the dream, the idea step, the card, the road);
 * `road` opens on the road itself, said in the ad's own words, with today's
 * stop their first creation and one button into it (components/FirstRoad.tsx,
 * well/product/33 §10). Owner, 2026-10-19. Unlike the others, the dice are
 * thrown by index.html, which has to know before any bundle loads which of the
 * two screens to draw; it keeps the arm in this same record, and leaves it on
 * `window.__kindlingLanding` too for storage that refuses. Nobody else gets an
 * arm: `landing()` is undefined for them.
 *
 * DECIDED 2026-10-21: everyone gets `quiz`. In W3 the road arm had 0 of 25
 * visitors reach the reward (quiz: 2 of 32, both gave an email). index.html no
 * longer throws dice and moves a browser kept on `road` to `quiz`; `?landing=road`
 * still draws the road, for QA, on that load only. Every event still carries
 * `landing`.
 */
interface Assignments {
  /** the dice's arm while the experiment ran; read by nothing now */
  idea_step?: Arm;
  /** an arm forced with `?idea_step=`, kept */
  idea_step_qa?: Arm;
  /** the dice's arm while the experiment ran; read by nothing now */
  place_step?: PlaceArm;
  /** an arm forced with `?place_step=`, kept */
  place_step_qa?: PlaceArm;
  landing?: LandingArm;
}

declare global {
  interface Window { __kindlingLanding?: LandingArm }
}

const ARMS = {
  idea_step: ['on', 'off'],
  place_step: ['machine', 'box'],
  landing: ['quiz', 'road'],
} as const;

type Name = keyof typeof ARMS;
type ArmOf<N extends Name> = (typeof ARMS)[N][number];

let memory: Assignments | null = null;

function read(): Assignments {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') return parsed as Assignments;
    }
  } catch { /* storage refused or a broken record: memory below */ }
  return memory ?? {};
}

function write(a: Assignments) {
  memory = a;
  try { localStorage.setItem(KEY, JSON.stringify(a)); } catch { /* memory holds it for this page */ }
}

function isArmOf<N extends Name>(name: N, x: unknown): x is ArmOf<N> {
  return (ARMS[name] as readonly string[]).includes(x as string);
}

/** `?<name>=<arm>` on this url, or nothing. */
function armFrom<N extends Name>(name: N, href: string): ArmOf<N> | undefined {
  try {
    const v = new URL(href).searchParams.get(name)?.trim().toLowerCase();
    return isArmOf(name, v) ? v : undefined;
  } catch { return undefined; }
}

const hrefOf = (href?: string) => href ?? (typeof window !== 'undefined' ? window.location.href : '');

/** `?idea_step=on|off` on this url, or nothing. */
export const ideaStepFrom = (href: string): Arm | undefined => armFrom('idea_step', href);

/**
 * This browser’s idea step: the url's override if there is one (kept), else an
 * override already kept, else on.
 */
export function ideaStep(opts: { href?: string } = {}): Arm {
  const href = hrefOf(opts.href);
  const all = read();
  const forced = href ? ideaStepFrom(href) : undefined;
  if (forced) {
    if (all.idea_step_qa !== forced) write({ ...all, idea_step_qa: forced });
    return forced;
  }
  if (!memory) memory = all;
  return isArmOf('idea_step', all.idea_step_qa) ? all.idea_step_qa : 'on';
}

/** `?place_step=machine|box` on this url, or nothing. */
export const placeStepFrom = (href: string): PlaceArm | undefined => armFrom('place_step', href);

/**
 * This browser’s place step: the url's override if there is one (kept), else
 * an override already kept, else the idea roll (`machine`).
 */
export function placeStep(opts: { href?: string } = {}): PlaceArm {
  const href = hrefOf(opts.href);
  const all = read();
  const forced = href ? placeStepFrom(href) : undefined;
  if (forced) {
    if (all.place_step_qa !== forced) write({ ...all, place_step_qa: forced });
    return forced;
  }
  if (!memory) memory = all;
  return isArmOf('place_step', all.place_step_qa) ? all.place_step_qa : 'machine';
}

/** `?landing=quiz|road` on this url, or nothing. */
export const landingFrom = (href: string): LandingArm | undefined => armFrom('landing', href);

/**
 * This browser’s landing arm, if index.html gave it one: the url's override
 * (kept), else what index.html kept, else what it left on the window for a
 * storage that refused. Never thrown here: only index.html knows who is eligible
 * before the first paint.
 */
export function landing(opts: { href?: string } = {}): LandingArm | undefined {
  const href = hrefOf(opts.href);
  const all = read();
  const forced = href ? landingFrom(href) : undefined;
  if (forced) {
    if (all.landing !== forced) write({ ...all, landing: forced });
    return forced;
  }
  if (isArmOf('landing', all.landing)) {
    if (!memory) memory = all;
    return all.landing;
  }
  const left = typeof window !== 'undefined' ? window.__kindlingLanding : undefined;
  return isArmOf('landing', left) ? left : undefined;
}

/** Tests only: forget the in-memory copy, as a new page would. */
export function resetExperimentsMemory() { memory = null; }
